import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Grid,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp } from "lucide-react";

function formatDateTime(isoString) {
  if (!isoString) return "Not available";

  return new Date(isoString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function statusColor(status) {
  switch (status) {
    case "completed":
      return "green";
    case "failed":
      return "red";
    case "processing":
      return "blue";
    case "pending":
      return "yellow";
    default:
      return "gray";
  }
}

function isWebhookDelayed(event) {
  if (!["pending", "processing"].includes(event.status)) return false;
  if (!event.created_at) return false;

  return Date.now() - new Date(event.created_at).getTime() > 2 * 60 * 1000;
}

export default function DataStatus() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [webhookData, setWebhookData] = useState(null);
  const [webhooksLoading, setWebhooksLoading] = useState(true);
  const [webhooksError, setWebhooksError] = useState(null);
  const [webhookFilters, setWebhookFilters] = useState({
    status: "",
    topic: "",
    from: "",
    to: "",
  });
  const [webhookPage, setWebhookPage] = useState(1);
  const [expandedWebhookIds, setExpandedWebhookIds] = useState(new Set());
  const toast = useToast();
  const isSyncing = data?.sync_status === "in_progress";
  const providerLabel = data?.provider_label || (data?.connected_provider === "service_m8" ? "ServiceM8" : "Jobber");
  const providerConnected = data?.provider_connected ?? data?.connected ?? data?.jobber_connected;
  const scheduledWorkLabel = data?.connected_provider === "service_m8" ? "Scheduled Work" : "Visits";
  const canRestartSync = data?.connected_provider === "service_m8" && isSyncing;
  const resyncButtonLabel = canRestartSync ? `Restart ${providerLabel} Sync` : isSyncing ? "Sync in progress" : `Resync ${providerLabel}`;

  const loadData = async () => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch("/api/data_status", {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || "Failed to load data status");
      }

      setData(payload.data);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to load data status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadWebhookEvents = async () => {
    try {
      setWebhooksLoading(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const params = new URLSearchParams();
      params.set("page", webhookPage);

      Object.entries(webhookFilters).forEach(([key, value]) => {
        if (value) params.set(key, value);
      });

      const response = await fetch(`/api/integration_webhook_events?${params.toString()}`, {
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.error || "Failed to load webhook events");
      }

      setWebhookData(payload);
      setWebhooksError(null);
    } catch (err) {
      setWebhooksError(err.message || "Failed to load webhook events");
    } finally {
      setWebhooksLoading(false);
    }
  };

  useEffect(() => {
    loadWebhookEvents();
  }, [webhookFilters, webhookPage]);

  useEffect(() => {
    const interval = setInterval(() => {
      loadData();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <VStack spacing={3}>
          <Spinner color="blue.500" />
          <Text color="gray.600">Loading data status...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box maxW="1200px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  const latestEvent = data?.latest_synced_event;
  const scheduleOpeningsNext = data?.schedule_openings_next || {};
  const visitsNext = data?.visits_next || {};
  const statusBanner = (() => {
    if (data?.needs_reauth) {
      return {
        status: "error",
        title: `${providerLabel} reconnect required`,
        message: `${providerLabel} needs to be reconnected before data can sync again.`
      };
    }

    if (!providerConnected) {
      return {
        status: "warning",
        title: `${providerLabel} not connected`,
        message: data?.connected === false
          ? `${providerLabel} connection is no longer valid. Reconnect your ${providerLabel} account to resume syncing.`
          : `${providerLabel} is not connected yet. Connect your ${providerLabel} account to populate this page.`
      };
    }

    if (isSyncing || resyncing) {
      return {
        status: "info",
        title: resyncing ? `Starting ${providerLabel} sync...` : `${providerLabel} sync in progress`,
        message: data?.sync_state_breakdown?.current_phase || `We’re updating ${providerLabel} data in the background. You can keep using the app while this runs.`
      };
    }

    return null;
  })();

  const handleResync = async () => {
    try {
      setResyncing(true);
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch("/api/jobber_status/resync", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ force: canRestartSync }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to start resync");
      }

      setData((prev) => ({
        ...(prev || {}),
        sync_status: "in_progress",
      }));

      toast({
        title: `${providerLabel} resync started`,
        description: `We’re syncing your ${providerLabel} data now. You can keep using the app while this runs.`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });
      loadData();
    } catch (err) {
      toast({
        title: "Resync failed",
        description: err.message || "Failed to start resync",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setResyncing(false);
    }
  };

  const updateWebhookFilter = (key, value) => {
    setWebhookFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setWebhookPage(1);
  };

  const clearWebhookFilters = () => {
    setWebhookFilters({
      status: "",
      topic: "",
      from: "",
      to: "",
    });
    setWebhookPage(1);
  };

  const toggleWebhookDetails = (eventId) => {
    setExpandedWebhookIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const webhookEvents = webhookData?.events || [];
  const webhookPagination = webhookData?.pagination || {};
  const webhookTopics = webhookData?.filters?.topics || [];
  const webhookStatuses = webhookData?.filters?.statuses || [];
  const delayedWebhookCount = webhookEvents.filter(isWebhookDelayed).length;

  return (
    <Box maxW="1200px" mx="auto" px={{ base: 4, md: 6 }} py={{ base: 6, md: 8 }}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" align="start" flexWrap="wrap" spacing={3}>
          <Box>
          <Heading size="lg">{providerLabel} Data Status</Heading>
          <Text mt={1} color="gray.600" fontSize="sm">
            Snapshot of synced worker, scheduled work, and schedule-opening data.
          </Text>
          </Box>
          <Button
            colorScheme="blue"
            onClick={handleResync}
            isLoading={resyncing}
            loadingText="Starting sync..."
            isDisabled={!providerConnected || data?.needs_reauth || (isSyncing && !canRestartSync) || resyncing}
          >
            {resyncButtonLabel}
          </Button>
        </HStack>

        {statusBanner && (
          <Alert status={statusBanner.status} variant="left-accent">
            <AlertIcon />
            <Box>
              <Text fontWeight="semibold">{statusBanner.title}</Text>
              <Text fontSize="sm">{statusBanner.message}</Text>
            </Box>
          </Alert>
        )}

        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
          <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg">
            <Text mb={3} fontSize="sm" color="gray.600" fontWeight="semibold">
              Schedule Openings In The Next...
            </Text>
            <HStack spacing={6} align="end">
              <Stat>
                <StatLabel fontSize="xs">Week</StatLabel>
                <StatNumber>{scheduleOpeningsNext.week || 0}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs">Month</StatLabel>
                <StatNumber>{scheduleOpeningsNext.month || 0}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs">Quarter</StatLabel>
                <StatNumber>{scheduleOpeningsNext.quarter || 0}</StatNumber>
              </Stat>
            </HStack>
          </Box>

          <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg">
            <Text mb={3} fontSize="sm" color="gray.600" fontWeight="semibold">
              {scheduledWorkLabel} In The Next...
            </Text>
            <HStack spacing={6} align="end">
              <Stat>
                <StatLabel fontSize="xs">Week</StatLabel>
                <StatNumber>{visitsNext.week || 0}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs">Month</StatLabel>
                <StatNumber>{visitsNext.month || 0}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel fontSize="xs">Quarter</StatLabel>
                <StatNumber>{visitsNext.quarter || 0}</StatNumber>
              </Stat>
            </HStack>
          </Box>

          <Stat p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg">
            <StatLabel>Last Sync</StatLabel>
            <StatNumber fontSize="lg">{formatDateTime(data?.last_synced_at)}</StatNumber>
          </Stat>
        </Grid>

        <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg">
          <Heading size="sm" mb={3}>Latest Synced Event</Heading>
          {latestEvent ? (
            <VStack align="stretch" spacing={2} fontSize="sm">
              <Text><strong>Title:</strong> {latestEvent.title || "Untitled"}</Text>
              <Text><strong>Type:</strong> {latestEvent.item_type || "Unknown"}</Text>
              <Text><strong>Status:</strong> {latestEvent.status || "Unknown"}</Text>
              <Text><strong>Client:</strong> {latestEvent.client_name || "Not available"}</Text>
              <Text><strong>Address:</strong> {latestEvent.address || "Not available"}</Text>
              <Text>
                <strong>Assigned Worker(s):</strong>{" "}
                {latestEvent.assigned_workers?.length > 0
                  ? latestEvent.assigned_workers.join(", ")
                  : "Not available"}
              </Text>
              <Text><strong>Start:</strong> {formatDateTime(latestEvent.start_at)}</Text>
              <Text><strong>End:</strong> {formatDateTime(latestEvent.end_at)}</Text>
            </VStack>
          ) : (
            <Text color="gray.600" fontSize="sm">No synced events found.</Text>
          )}
        </Box>

        <Box p={5} bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg">
          <HStack justify="space-between" align="start" flexWrap="wrap" spacing={3} mb={4}>
            <Box>
              <Heading size="sm">Recent {providerLabel} Webhooks</Heading>
              <Text mt={1} color="gray.600" fontSize="sm">
                Incoming webhook calls received for this {providerLabel} account.
              </Text>
            </Box>
            <Button size="sm" variant="outline" onClick={loadWebhookEvents} isLoading={webhooksLoading}>
              Refresh
            </Button>
          </HStack>

          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={3} mb={4}>
            <Select
              size="sm"
              value={webhookFilters.status}
              onChange={(event) => updateWebhookFilter("status", event.target.value)}
            >
              <option value="">All statuses</option>
              {webhookStatuses.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>
            <Select
              size="sm"
              value={webhookFilters.topic}
              onChange={(event) => updateWebhookFilter("topic", event.target.value)}
            >
              <option value="">All topics</option>
              {webhookTopics.map((topic) => (
                <option key={topic} value={topic}>{topic}</option>
              ))}
            </Select>
            <Input
              size="sm"
              type="date"
              value={webhookFilters.from}
              onChange={(event) => updateWebhookFilter("from", event.target.value)}
            />
            <HStack spacing={2}>
              <Input
                size="sm"
                type="date"
                value={webhookFilters.to}
                onChange={(event) => updateWebhookFilter("to", event.target.value)}
              />
              <Button size="sm" variant="ghost" onClick={clearWebhookFilters}>
                Clear
              </Button>
            </HStack>
          </Grid>

          {webhooksError && (
            <Alert status="error" mb={4}>
              <AlertIcon />
              {webhooksError}
            </Alert>
          )}

          {delayedWebhookCount > 0 && (
            <Alert status="warning" mb={4} variant="left-accent">
              <AlertIcon />
              <Box>
                <Text fontWeight="semibold">Webhook processing is delayed</Text>
                <Text fontSize="sm">
                  {delayedWebhookCount} webhook {delayedWebhookCount === 1 ? "has" : "have"} been pending or processing for more than 2 minutes. Check that the background worker is running and polling the critical queue.
                </Text>
              </Box>
            </Alert>
          )}

          {webhooksLoading && !webhookData ? (
            <HStack spacing={3} color="gray.600">
              <Spinner size="sm" />
              <Text fontSize="sm">Loading {providerLabel} webhooks...</Text>
            </HStack>
          ) : webhookEvents.length > 0 ? (
            <>
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Received</Th>
                      <Th>Topic</Th>
                      <Th>Status</Th>
                      <Th>Item ID</Th>
                      <Th>Processed</Th>
                      <Th>Error</Th>
                      <Th textAlign="right">Details</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {webhookEvents.map((event) => {
                      const isExpanded = expandedWebhookIds.has(event.id);

                      return (
                        <React.Fragment key={event.id}>
                          <Tr>
                            <Td whiteSpace="nowrap">{formatDateTime(event.created_at)}</Td>
                            <Td whiteSpace="nowrap">{event.topic}</Td>
                            <Td>
                              <Badge colorScheme={statusColor(event.status)}>{event.status}</Badge>
                            </Td>
                            <Td maxW="260px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                              {event.item_id}
                            </Td>
                            <Td whiteSpace="nowrap">{formatDateTime(event.processed_at)}</Td>
                            <Td maxW="240px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                              {event.error_message || "-"}
                            </Td>
                            <Td textAlign="right">
                              <IconButton
                                aria-label={isExpanded ? "Hide webhook details" : "Show webhook details"}
                                icon={isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                size="xs"
                                variant="ghost"
                                onClick={() => toggleWebhookDetails(event.id)}
                              />
                            </Td>
                          </Tr>
                          {isExpanded && (
                            <Tr>
                              <Td colSpan={7} bg="gray.50">
                                {event.error_message && (
                                  <Box mb={3}>
                                    <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={1}>
                                      Error
                                    </Text>
                                    <Text fontSize="sm" color="red.700">{event.error_message}</Text>
                                  </Box>
                                )}
                                <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={1}>
                                  Payload
                                </Text>
                                <Box
                                  as="pre"
                                  p={3}
                                  bg="gray.900"
                                  color="gray.50"
                                  borderRadius="md"
                                  fontSize="xs"
                                  overflowX="auto"
                                  whiteSpace="pre-wrap"
                                >
                                  {JSON.stringify(event.payload || {}, null, 2)}
                                </Box>
                              </Td>
                            </Tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>

              <HStack justify="space-between" mt={4} flexWrap="wrap" spacing={3}>
                <Text fontSize="sm" color="gray.600">
                  Showing page {webhookPagination.page || 1} of {webhookPagination.total_pages || 1}
                  {" "}({webhookPagination.total_count || 0} total)
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setWebhookPage((prev) => Math.max(1, prev - 1))}
                    isDisabled={(webhookPagination.page || 1) <= 1 || webhooksLoading}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setWebhookPage((prev) => prev + 1)}
                    isDisabled={
                      webhooksLoading ||
                      (webhookPagination.page || 1) >= (webhookPagination.total_pages || 1)
                    }
                  >
                    Next
                  </Button>
                </HStack>
              </HStack>
            </>
          ) : (
            <Text color="gray.600" fontSize="sm">No {providerLabel} webhook events found.</Text>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
